import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Clock, Circle, Sparkles, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { createLogger } from '../lib/logger';
import { timelineService, aiTimelineService } from '../lib/supabase';
import { TimelineEvent, WeddingInvitation } from '../types/invitation';
import type { GeneratedTask } from '../lib/supabase/aiTimelineService';
import useCollaborationPermissions from '../hooks/useCollaborationPermissions';
import TimelineTaskCard from './TimelineTaskCard';
import TaskEditor from './TaskEditor';
import AITimelinePreview from './AITimelinePreview';
import { UpgradePrompt } from './UpgradePrompt';
import PrimaryButton from './themed/PrimaryButton';
import ThemedCard from './themed/ThemedCard';
import TimelineVisualization from './timeline/TimelineVisualization';
import TimelineControls from './timeline/TimelineControls';
import InvitationContextBanner from './InvitationContextBanner';

const log = createLogger('timeline');

interface PlanningTimelineProps {
  invitationId: string | null;
  weddingDate: string | null;
  currentInvitation?: WeddingInvitation; // 當前邀請函資訊
}

const PlanningTimeline: React.FC<PlanningTimelineProps> = ({
  invitationId,
  weddingDate,
  currentInvitation
}) => {
  const { user } = useAuth();
  const { canUseAITimeline } = useSubscription();
  const { canEdit, loading: permissionsLoading } = useCollaborationPermissions(currentInvitation?.id || null);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTaskEditorOpen, setIsTaskEditorOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<TimelineEvent | null>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'list'>('timeline');

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState<GeneratedTask[] | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isInserting, setIsInserting] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [localGenerationBump, setLocalGenerationBump] = useState(0);


  // 載入時間軸事件
  const loadTimelineEvents = async () => {
    if (!invitationId || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const events = await timelineService.getTimelineEvents(invitationId);
      setTimelineEvents(events);
      setError(null);
    } catch (err) {
      log.error('載入時間軸事件失敗:', err);
      setError('載入時間軸任務失敗，請稍後再試。');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimelineEvents();
  }, [invitationId, user]);

  // 新增任務
  const handleAddTask = async (eventData: Omit<TimelineEvent, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => {
    log.debug('handleAddTask: 收到的事件資料', eventData);
    log.debug('handleAddTask: 當前 invitationId', invitationId);
    
    if (!invitationId) {
      throw new Error('無法新增任務：未找到邀請函 ID');
    }
    
    try {
      await timelineService.addTimelineEvent(eventData);
      await loadTimelineEvents(); // 重新載入列表
    } catch (error) {
      log.error('新增任務失敗:', error);
      throw error;
    }
  };

  // 更新任務
  const handleUpdateTask = async (eventId: string, updates: Partial<TimelineEvent>) => {
    try {
      await timelineService.updateTimelineEvent(eventId, updates);
      await loadTimelineEvents(); // 重新載入列表
    } catch (error) {
      log.error('更新任務失敗:', error);
      throw error;
    }
  };

  // 刪除任務
  const handleDeleteTask = async (eventId: string) => {
    try {
      await timelineService.deleteTimelineEvent(eventId);
      await loadTimelineEvents(); // 重新載入列表
    } catch (error) {
      log.error('刪除任務失敗:', error);
      throw error;
    }
  };

  // 切換任務完成狀態
  const handleToggleComplete = async (eventId: string, isCompleted: boolean) => {
    try {
      await timelineService.updateTaskCompletion(eventId, isCompleted);
      await loadTimelineEvents(); // 重新載入列表
    } catch (error) {
      log.error('更新任務完成狀態失敗:', error);
    }
  };

  // 編輯任務
  const handleEditTask = (event: TimelineEvent) => {
    setEditingEvent(event);
    setIsTaskEditorOpen(true);
  };

  // 關閉任務編輯器
  const handleCloseTaskEditor = () => {
    setIsTaskEditorOpen(false);
    setEditingEvent(null);
  };

  // 開啟新增任務模式
  const handleOpenAddTask = () => {
    setEditingEvent(null);
    setIsTaskEditorOpen(true);
  };

  const AI_GENERATION_LIMIT = 3;
  const aiGenerationCount = (currentInvitation?.ai_generation_count ?? 0) + localGenerationBump;
  const aiGenerationsRemaining = AI_GENERATION_LIMIT - aiGenerationCount;

  const handleAIGenerate = async () => {
    if (!canUseAITimeline()) {
      setShowUpgradePrompt(true);
      return;
    }

    if (!currentInvitation) return;

    if (!currentInvitation.weddingDate) {
      setGenerateError('請先在邀請函中設定婚禮日期');
      return;
    }

    if (aiGenerationsRemaining <= 0) {
      setGenerateError('已達到 AI 生成次數上限（3 次）');
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);

    const result = await aiTimelineService.generateTimeline(currentInvitation, timelineEvents);

    if (result.success && result.tasks) {
      setGeneratedTasks(result.tasks);
      setShowPreview(true);
    } else {
      setGenerateError(result.error || 'AI 生成失敗，請稍後再試');
    }

    setIsGenerating(false);
  };

  const handleConfirmInsert = async (selectedTasks: GeneratedTask[]) => {
    if (!invitationId) return;

    setIsInserting(true);

    const maxOrder = timelineEvents.length > 0
      ? Math.max(...timelineEvents.map(e => e.display_order))
      : 0;

    try {
      for (let i = 0; i < selectedTasks.length; i++) {
        await timelineService.addTimelineEvent({
          invitation_id: invitationId,
          title: selectedTasks[i].title,
          description: selectedTasks[i].description,
          category: selectedTasks[i].category,
          target_date: selectedTasks[i].target_date,
          display_order: maxOrder + i + 1,
          is_completed: false,
        });
      }
      if (invitationId) {
        await aiTimelineService.incrementGenerationCount(invitationId);
        setLocalGenerationBump(prev => prev + 1);
      }
      setShowPreview(false);
      setGeneratedTasks(null);
      await loadTimelineEvents();
    } catch (err) {
      log.error('批量插入任務失敗:', err);
      await loadTimelineEvents();
    } finally {
      setIsInserting(false);
    }
  };

  // 按日期分組事件
  const groupEventsByDate = (events: TimelineEvent[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const nextMonth = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    const groups = {
      overdue: [] as TimelineEvent[],
      today: [] as TimelineEvent[],
      tomorrow: [] as TimelineEvent[],
      thisWeek: [] as TimelineEvent[],
      thisMonth: [] as TimelineEvent[],
      later: [] as TimelineEvent[]
    };

    events.forEach(event => {
      const eventDate = new Date(event.target_date);
      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

      if (eventDateOnly < today && !event.is_completed) {
        groups.overdue.push(event);
      } else if (eventDateOnly.getTime() === today.getTime()) {
        groups.today.push(event);
      } else if (eventDateOnly.getTime() === tomorrow.getTime()) {
        groups.tomorrow.push(event);
      } else if (eventDate <= nextWeek) {
        groups.thisWeek.push(event);
      } else if (eventDate <= nextMonth) {
        groups.thisMonth.push(event);
      } else {
        groups.later.push(event);
      }
    });

    return groups;
  };

  // 統計資訊
  const getStats = () => {
    const total = timelineEvents.length;
    const completed = timelineEvents.filter(e => e.is_completed).length;
    const pending = total - completed;
    const overdue = timelineEvents.filter(e => {
      const eventDate = new Date(e.target_date);
      const today = new Date();
      return eventDate < today && !e.is_completed;
    }).length;

    return { total, completed, pending, overdue };
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-wedding-text-body">載入時間軸中...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <PrimaryButton onClick={loadTimelineEvents}>
            重新載入
          </PrimaryButton>
        </div>
      </div>
    );
  }

  if (!invitationId) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-wedding-text-body">
            未找到活躍邀請函，請先創建邀請函後再使用時間規劃功能。
          </p>
        </div>
      </div>
    );
  }

  const stats = getStats();
  const groupedEvents = groupEventsByDate(timelineEvents);

  // 渲染內容區域
  const renderContent = () => {
    if (isGenerating) {
      return (
        <ThemedCard className="p-8 text-center">
          <Sparkles className="w-12 h-12 text-wedding-pink mx-auto mb-4 animate-pulse" />
          <h3 className="text-lg font-semibold text-wedding-text-title mb-2">
            AI 正在規劃您的婚禮時間軸
          </h3>
          <p className="text-wedding-text-body mb-4 text-sm">
            根據您的婚禮日期和活動安排，生成專屬的籌備計畫...
          </p>
          <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden mx-auto">
            <div className="h-full bg-wedding-pink rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
        </ThemedCard>
      );
    }

    if (timelineEvents.length === 0) {
      return (
        <ThemedCard className="p-8 text-center">
          <Clock className="w-12 h-12 text-wedding-pink mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-wedding-text-title mb-2">
            開始規劃您的婚禮
          </h3>
          <p className="text-wedding-text-body mb-6">
            目前沒有時間軸任務，可以用 AI 自動生成婚禮籌備計畫，或手動新增任務。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <div className="flex flex-col items-center gap-1">
              <PrimaryButton
                onClick={handleAIGenerate}
                disabled={isGenerating || aiGenerationsRemaining <= 0}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                AI 智慧生成
              </PrimaryButton>
              {canUseAITimeline() && (
                <span className="text-xs text-gray-400">
                  剩餘 {aiGenerationsRemaining} 次
                </span>
              )}
            </div>
            <button
              onClick={handleOpenAddTask}
              className="px-4 py-2 text-wedding-text-body border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              手動建立任務
            </button>
          </div>
          {generateError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{generateError}</p>
            </div>
          )}
        </ThemedCard>
      );
    }

    if (viewMode === 'timeline') {
      return (
        <TimelineVisualization
          events={timelineEvents}
          weddingDate={weddingDate || ''}
          onEventEdit={handleEditTask}
          onEventToggle={handleToggleComplete}
          onEventDelete={handleDeleteTask}
        />
      );
    } else {
      // 原有的列表視圖
      return (
        <div className="space-y-6">
          {/* 已過期 */}
          {groupedEvents.overdue.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-red-500 mb-3 flex items-center gap-2">
                <Circle className="w-5 h-5" />
                已過期 ({groupedEvents.overdue.length})
              </h2>
              <div className="space-y-3">
                {groupedEvents.overdue.map(event => (
                  <TimelineTaskCard
                    key={event.id}
                    event={event}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 今天 */}
          {groupedEvents.today.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-wedding-text-title mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                今天 ({groupedEvents.today.length})
              </h2>
              <div className="space-y-3">
                {groupedEvents.today.map(event => (
                  <TimelineTaskCard
                    key={event.id}
                    event={event}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 明天 */}
          {groupedEvents.tomorrow.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-wedding-text-title mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                明天 ({groupedEvents.tomorrow.length})
              </h2>
              <div className="space-y-3">
                {groupedEvents.tomorrow.map(event => (
                  <TimelineTaskCard
                    key={event.id}
                    event={event}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 本週 */}
          {groupedEvents.thisWeek.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-wedding-text-title mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                本週 ({groupedEvents.thisWeek.length})
              </h2>
              <div className="space-y-3">
                {groupedEvents.thisWeek.map(event => (
                  <TimelineTaskCard
                    key={event.id}
                    event={event}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 本月 */}
          {groupedEvents.thisMonth.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-wedding-text-title mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                本月 ({groupedEvents.thisMonth.length})
              </h2>
              <div className="space-y-3">
                {groupedEvents.thisMonth.map(event => (
                  <TimelineTaskCard
                    key={event.id}
                    event={event}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 之後 */}
          {groupedEvents.later.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-wedding-text-title mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                之後 ({groupedEvents.later.length})
              </h2>
              <div className="space-y-3">
                {groupedEvents.later.map(event => (
                  <TimelineTaskCard
                    key={event.id}
                    event={event}
                    onToggleComplete={handleToggleComplete}
                    onEdit={handleEditTask}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* 邀請函上下文橫幅 */}
      <InvitationContextBanner 
        invitation={currentInvitation || null} 
      />
      
      {/* 權限載入中 */}
      {permissionsLoading && (
        <div className="text-center py-4">
          <div className="text-wedding-text-body">檢查權限中...</div>
        </div>
      )}
      
      {/* 無編輯權限提示 */}
      {!permissionsLoading && !canEdit && (
        <ThemedCard className="p-6 text-center">
          <div className="text-wedding-text-body">
            您沒有權限管理此邀請函的時間規劃。請聯絡邀請函擁有者。
          </div>
        </ThemedCard>
      )}
      
      {/* 主要內容區域 - 需要編輯權限 */}
      {canEdit && (
        <>
      {/* 頁面標題和控制 */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {/* 新增任務按鈕 */}
            <PrimaryButton
              onClick={handleOpenAddTask}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              新增任務
            </PrimaryButton>
            {/* AI 生成按鈕 */}
            {canUseAITimeline() && aiGenerationsRemaining > 0 && (
              <button
                onClick={handleAIGenerate}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 text-sm text-wedding-pink border border-wedding-pink/30 rounded-lg hover:bg-wedding-pink/5 transition-colors disabled:opacity-50"
                title={`剩餘 ${aiGenerationsRemaining} 次`}
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                {isGenerating ? 'AI 生成中...' : `AI 智慧生成 (${aiGenerationsRemaining})`}
              </button>
            )}
          </div>
          
          {/* 視圖切換控制 */}
          <TimelineControls 
            viewMode={viewMode} 
            onViewChange={setViewMode} 
          />
        </div>

        {/* 統計卡片（僅在列表視圖顯示）*/}
        {viewMode === 'list' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <ThemedCard className="p-4 text-center">
              <div className="text-2xl font-bold text-wedding-text-title">{stats.total}</div>
              <div className="text-sm text-wedding-text-body">總任務</div>
            </ThemedCard>
            <ThemedCard className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
              <div className="text-sm text-wedding-text-body">已完成</div>
            </ThemedCard>
            <ThemedCard className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.pending}</div>
              <div className="text-sm text-wedding-text-body">進行中</div>
            </ThemedCard>
            <ThemedCard className="p-4 text-center">
              <div className="text-2xl font-bold text-red-500">{stats.overdue}</div>
              <div className="text-sm text-wedding-text-body">已過期</div>
            </ThemedCard>
          </div>
        )}
      </div>

      {/* 內容區域 */}
      {renderContent()}

      {/* 任務編輯器 */}
      <TaskEditor
        isOpen={isTaskEditorOpen}
        onClose={handleCloseTaskEditor}
        onSave={handleAddTask}
        onUpdate={handleUpdateTask}
        onDelete={handleDeleteTask}
        editingEvent={editingEvent}
        invitationId={invitationId}
      />

      {/* AI 預覽 Dialog */}
      {generatedTasks && (
        <AITimelinePreview
          isOpen={showPreview}
          onClose={() => {
            setShowPreview(false);
            setGeneratedTasks(null);
          }}
          tasks={generatedTasks}
          onConfirm={handleConfirmInsert}
          isInserting={isInserting}
        />
      )}

      {/* 升級提示 */}
      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        message="AI 智慧生成功能需要標準方案或以上。升級方案即可使用 AI 自動規劃婚禮時間軸。"
      />

      {/* 生成錯誤提示（在有任務時顯示） */}
      {generateError && timelineEvents.length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{generateError}</p>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default PlanningTimeline;